import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/v2/PageHeader";
import { PlanUsageBanner } from "@/components/v2/PlanUsageBanner";
import { StatCard } from "@/components/v2/StatCard";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { Banner } from "@/components/v2/Banner";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { FilterPill } from "@/components/v2/FilterPill";
import { EmptyState } from "@/components/v2/EmptyState";
import { BookingSidePanel } from "@/components/v2/BookingSidePanel";
import { NewBookingPanel } from "@/components/v2/NewBookingPanel";
import { EditBookingPanel } from "@/components/v2/EditBookingPanel";
import { useBookingDetail } from "@/lib/booking-detail";
import { parseDateLocal, formatPreferredDate } from "@/lib/booking-date";

/**
 * Bookings — Pencil frames `0aPHl` + `AD4ny` (verification queue lens).
 *
 *   yellow Banner    : verifying queue size > 0 with "Review queue" CTA
 *   StatCards row    : This week / Pending review / Confirmed value MTD / Deposits outstanding
 *   FilterPill row   : status pills (domain semantics, stay above the table)
 *   DataTable        : sort + search + selection + pagination + CSV export +
 *                       column visibility + density toggle, all in one
 *                       AG-Grid-class table.
 */

type BookingRow = {
  id?: string;
  serviceName?: string | null;
  customerName?: string;
  customerEmail?: string | null;
  preferredDate?: string | null;
  calendarDates?: { fieldId: string; label: string; date: string }[];
  status?: string;
  paymentStatus?: string | null;
  price?: number | string;
  currency?: string;
};

type Filter = "all" | "pending" | "confirmed" | "completed" | "cancelled" | "verifying";

const FILTERS: { key: Filter; label: string; tone?: "warning" }[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "verifying", label: "Verifying" },
  { key: "confirmed", label: "Confirmed" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const DEFAULT_PAGE_SIZE = 30;


export function Bookings() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useTenantMe();
  const tenantCurrency = tenant.data?.tenant.currency ?? "PHP";
  // Panel focus lives in `?focus=<id>` — same pattern as Clients so deep
  // links and shared URLs work without a separate detail route.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [newOpen, setNewOpen] = useState(false);
  // Edit/reschedule panel state. When set, opens an EditBookingPanel on top of
  // the side panel; the latter stays mounted so the user can return to it.
  const [editId, setEditId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<"edit" | "reschedule">("edit");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const openPanel = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("focus", id);
    else next.delete("focus");
    setSearchParams(next, { replace: true });
  };
  const closePanel = () => openPanel(null);

  const list = useQuery({
    queryKey: ["bookings", filter, page, pageSize],
    enabled: !!accessToken,
    queryFn: async () => {
      const params: Record<string, string | number> = { page, size: pageSize };
      if (filter === "verifying") params.paymentStatus = "verifying";
      return unwrap(await api.GET("/api/v1/bookings", { params: { query: params } }));
    },
  });

  // Stats query is the dashboard overview — its KPIs apply to this page too.
  const overview = useQuery({
    queryKey: ["dashboard.overview"],
    enabled: !!accessToken,
    queryFn: async () => unwrap(await api.GET("/api/v1/dashboard/overview", {})),
  });

  // Status pill filter is client-side. The TanStack-backed DataTable
  // does its own global search + sort on whatever rows we hand it.
  const statusFilteredRows = useMemo(() => {
    const rows = (list.data?.content ?? []) as BookingRow[];
    if (filter === "all" || filter === "verifying") return rows;
    return rows.filter((r) => (r.status ?? "").toLowerCase() === filter);
  }, [list.data?.content, filter]);

  const verifyingCount = Number(overview.data?.verifyingQueueSize ?? 0);
  const confirmedToday = Number(overview.data?.confirmedToday ?? 0);

  const columns: Column<BookingRow>[] = [
    {
      key: "ref",
      header: "Ref",
      width: "92",
      sortable: false,
      cell: (r) => (
        <Link
          to={`/bookings?focus=${r.id}`}
          className="font-mono text-xs hover:underline"
        >
          #{r.id?.slice(0, 8)}
        </Link>
      ),
      csvCell: (r) => r.id?.slice(0, 8) ?? "",
      filter: { type: "text", placeholder: "Ref…" },
      filterAccessor: (r) => r.id ?? "",
    },
    {
      key: "client",
      header: "Client & service",
      cell: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-sm leading-tight truncate">
            {r.customerName || "(walk-in)"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {r.serviceName ?? "—"}
          </p>
        </div>
      ),
      csvCell: (r) => `${r.customerName ?? "(walk-in)"} · ${r.serviceName ?? ""}`,
      filter: { type: "text", placeholder: "Client or service…" },
      filterAccessor: (r) =>
        `${r.customerName ?? ""} ${r.serviceName ?? ""}`.trim(),
    },
    {
      key: "when",
      header: "Date",
      width: "180",
      sortAccessor: (r) =>
        r.preferredDate ? parseDateLocal(r.preferredDate) : null,
      cell: (r) => (
        <span className="text-sm whitespace-nowrap">
          {r.preferredDate ? (
            formatPreferredDate(r.preferredDate)
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>
      ),
      csvCell: (r) => r.preferredDate ?? "",
      filter: { type: "date" },
      filterAccessor: (r) =>
        r.preferredDate ? parseDateLocal(r.preferredDate) : null,
    },
    {
      key: "status",
      header: "Status",
      width: "130",
      cell: (r) => <StatusBadge label={r.status ?? "—"} />,
      csvCell: (r) => r.status ?? "",
      filter: {
        type: "select",
        options: [
          { value: "pending", label: "Pending" },
          { value: "confirmed", label: "Confirmed" },
          { value: "completed", label: "Completed" },
          { value: "cancelled", label: "Cancelled" },
        ],
      },
      filterAccessor: (r) => r.status ?? "",
    },
    {
      key: "payment",
      header: "Payment",
      width: "130",
      cell: (r) => <StatusBadge label={r.paymentStatus ?? "—"} />,
      csvCell: (r) => r.paymentStatus ?? "",
      filter: {
        type: "select",
        options: [
          { value: "verifying", label: "Verifying" },
          { value: "verified", label: "Verified" },
          { value: "rejected", label: "Rejected" },
          { value: "unpaid", label: "Unpaid" },
        ],
      },
      filterAccessor: (r) => r.paymentStatus ?? "",
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      width: "140",
      sortAccessor: (r) =>
        typeof r.price === "string" ? Number(r.price) : (r.price ?? 0),
      cell: (r) => (
        <span className="font-semibold text-sm">{formatMoney(r.currency, r.price)}</span>
      ),
      csvCell: (r) => `${r.currency ?? ""} ${r.price ?? ""}`.trim(),
      filter: { type: "number" },
      filterAccessor: (r) =>
        typeof r.price === "string" ? parseFloat(r.price) : (r.price ?? 0),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Bookings"
        subtitle="Every appointment, request, and event in one feed."
        actions={
          <Button size="sm" onClick={() => setNewOpen(true)}>
            + New booking
          </Button>
        }
      />

      <PlanUsageBanner />

      {verifyingCount > 0 && (
        <Banner
          tone="warning"
          title={`${verifyingCount} booking${verifyingCount > 1 ? "s" : ""} need payment verification`}
          description="Review uploaded receipts to confirm pending bookings."
          action={
            <Button size="sm" onClick={() => setFilter("verifying")}>
              Review queue
            </Button>
          }
        />
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          label="This week"
          value={list.data?.totalElements ?? 0}
          hint={confirmedToday ? `${confirmedToday} confirmed today` : "0 confirmed today"}
        />
        <StatCard
          label="Pending review"
          value={verifyingCount}
          hint={verifyingCount ? "Receipts awaiting verification" : "All caught up"}
        />
        <StatCard
          label="Confirmed value · today"
          value={formatMoney(
            tenantCurrency,
            overview.data?.revenueToday as unknown as number | string | undefined,
          )}
          hint={`${confirmedToday} confirmed`}
        />
      </div>

      {/* Status filter pills sit above the table — they're domain
          semantics, separate from the table's global search input. */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <FilterPill
            key={f.key}
            active={filter === f.key}
            onClick={() => {
              setFilter(f.key);
              setPage(0);
              setSelectedIds(new Set());
            }}
            count={
              f.key === "verifying" && verifyingCount ? verifyingCount : undefined
            }
          >
            {f.label}
          </FilterPill>
        ))}
      </div>

      {list.isError && (
        <p className="text-sm text-destructive">{(list.error as Error).message}</p>
      )}

      {list.data && (
        <DataTable
          columns={columns}
          rows={statusFilteredRows}
          rowKey={(r) => r.id ?? Math.random().toString()}
          onRowClick={(r) => {
            if (r.id) openPanel(r.id);
          }}
          expandable={(r) => <ExpandedBookingPanel row={r} />}
          loading={list.isLoading}
          sortable
          resizable
          hideable
          selection={{
            rowIds: selectedIds,
            onChange: setSelectedIds,
            actions: (rows) => (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    // eslint-disable-next-line no-console
                    console.log("TODO: bulk verify", rows.map((r) => r.id));
                  }}
                >
                  Mark as verified
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-destructive"
                  onClick={() => {
                    // eslint-disable-next-line no-console
                    console.log("TODO: bulk cancel", rows.map((r) => r.id));
                  }}
                >
                  Cancel selected
                </Button>
              </>
            ),
          }}
          pagination={{
            pageIndex: page,
            pageSize,
            pageCount: list.data.totalPages ?? 1,
            totalRows: list.data.totalElements,
            onPageChange: (p) => {
              setPage(p);
              setSelectedIds(new Set());
            },
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(0);
              setSelectedIds(new Set());
            },
          }}
          exportCsv={{
            filename: `bookings-${new Date().toISOString().slice(0, 10)}.csv`,
          }}
          emptyState={
            <div className="p-8">
              <EmptyState
                title={filter === "verifying" ? "Queue is empty" : "No bookings yet"}
                description={
                  filter === "verifying"
                    ? "All payments are verified. Nice work."
                    : "When clients schedule appointments, they'll appear here."
                }
                action={
                  filter !== "verifying" && (
                    <Button size="sm" onClick={() => setNewOpen(true)}>
                      + New booking
                    </Button>
                  )
                }
              />
            </div>
          }
        />
      )}

      <BookingSidePanel
        bookingId={focusId}
        onClose={closePanel}
        onEdit={(id) => {
          // Hand off from view-panel to edit-panel: close the side
          // panel so we don't stack two Radix Dialogs (which deadlocks
          // pointer-events on the second one), and remember the id so
          // we can re-open the side panel when the edit closes.
          setEditMode("edit");
          setEditId(id);
          openPanel(null);
        }}
        onReschedule={(id) => {
          setEditMode("reschedule");
          setEditId(id);
          openPanel(null);
        }}
      />
      <NewBookingPanel
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(id) => openPanel(id)}
      />
      <EditBookingPanel
        bookingId={editId}
        mode={editMode}
        onClose={() => {
          const id = editId;
          setEditId(null);
          // Re-open the side panel on the same booking so the user
          // lands back where they came from with fresh data.
          if (id) openPanel(id);
        }}
      />
    </div>
  );
}

/**
 * Pencil component `hJNwM` (`v2/ExpandedFieldsPanel`). Sits inside an expanded
 * booking row. Header strip ("CUSTOM FIELDS · <Service>" + "Edit fields" link)
 * + 4-column field grid. When the booking has any per-booking custom field
 * values, a second strip shows those (lazy-loaded via `useBookingDetail` so
 * we don't re-fetch on row collapse). Snapshot-backed values keep rendering
 * even if a schema field has since been deleted.
 */
function ExpandedBookingPanel({ row }: { row: BookingRow }) {
  const window = row.preferredDate ? formatPreferredDate(row.preferredDate) : "—";

  const detail = useBookingDetail(row.id);
  const cfv = detail.data?.customFieldValues ?? [];

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Booking details ·{" "}
          <span className="text-foreground/80">{row.serviceName ?? "Service"}</span>
        </p>
        <Link
          to={`/bookings?focus=${row.id}`}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          Edit fields →
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 px-4 py-4">
        <FieldCell label="Customer" value={row.customerName || "(walk-in)"} />
        <FieldCell label="Email" value={row.customerEmail ?? "—"} />
        <FieldCell label="Date" value={window} />
        <FieldCell
          label="Payment"
          value={<StatusBadge label={row.paymentStatus ?? "—"} />}
        />
      </div>

      {cfv.length > 0 && (
        <>
          <div className="border-t border-border px-4 py-2 bg-muted/20">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              Custom fields
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 px-4 py-4">
            {cfv.map((f) => (
              <FieldCell
                key={f.fieldId}
                label={f.label}
                value={
                  f.value && f.value.trim() !== "" ? (
                    f.type === "checkbox" ? (
                      f.value === "true" ? "Yes" : "No"
                    ) : f.type === "date" || f.type === "datetime" ? (
                      formatPreferredDate(f.value)
                    ) : (
                      f.value
                    )
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )
                }
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FieldCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p className="mt-1 text-sm truncate">{value}</p>
    </div>
  );
}
